import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AccessAdminBootstrap } from "./AccessAdminBootstrap";
import { PrincipalAdminLink } from "./PrincipalAdminLink";

// Passwords stay only in this mounted dialog's memory, never browser storage.
export function InitialAdminPasswordReset({ tenantId, onBusyChange }) {
  const [target, setTarget] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [revision, setRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const pending = useRef(null);
  const submitting = useRef(false);
  const mounted = useRef(false);
  const endpoint = `/platform/tenants/${tenantId}/initial-admin`;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; pending.current = null; };
  }, []);
  useEffect(() => {
    let active = true;
    setTarget(null); setLoadError("");
    api.get(endpoint).then(({ data }) => { if (active) setTarget(data); })
      .catch((e) => { if (active) setLoadError(apiErr(e)); });
    return () => { active = false; };
  }, [endpoint, revision]);

  const close = () => {
    if (submitting.current) return;
    pending.current = null;
    setPassword(""); setConfirm(""); setError(""); setUncertain(false); setOpen(false);
    setRevision((r) => r + 1);
  };
  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current || !target?.eligible) return;
    if (!pending.current) {
      if (!password.trim() || Array.from(password).length < 6) {
        setError("A senha deve ter ao menos 6 caracteres."); return;
      }
      if (new TextEncoder().encode(password).length > 72) {
        setError("A senha deve ter no máximo 72 bytes em UTF-8."); return;
      }
      if (password !== confirm) {
        setError("A nova senha e a confirmação não conferem."); return;
      }
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      pending.current = {
        key: Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(""),
        payload: { new_password: password, confirm_password: confirm,
          expected_user_id: target.user.id, expected_token_version: target.expected_token_version },
      };
    }
    const attempt = pending.current;
    submitting.current = true; setBusy(true); onBusyChange(true); setError("");
    try {
      const result = await api.post(endpoint + "/reset-password", attempt.payload,
        { headers: { "Idempotency-Key": attempt.key } });
      if (!mounted.current) return;
      pending.current = null; setPassword(""); setConfirm(""); setUncertain(false);
      setOpen(false); setRevision((r) => r + 1);
      if (result?.data?.audit_pending) {
        toast.warning("Senha alterada e sessões revogadas. Auditoria pendente em recuperação automática. Não redefina novamente.");
      } else toast.success(`Senha de ${target.user.name || target.user.email} redefinida em ${target.tenant.name}. Sessões anteriores revogadas.`);
    } catch (e) {
      if (!mounted.current) return;
      if ([400, 404, 409, 422].includes(e?.response?.status)) {
        pending.current = null; setPassword(""); setConfirm(""); setUncertain(false);
        setError(apiErr(e)); setRevision((r) => r + 1);
      } else {
        setUncertain(true);
        setError("Não foi possível confirmar o resultado. Repita a confirmação com os mesmos dados; a operação não será aplicada duas vezes.");
      }
    } finally {
      submitting.current = false;
      if (mounted.current) { setBusy(false); onBusyChange(false); }
    }
  };

  return (
    <section className="mt-4 border-t border-slate-700 pt-4 space-y-2">
      <Button type="button" data-testid="initial-admin-reset-open"
        disabled={!target?.eligible || busy}
        onClick={() => { setError(""); setOpen(true); }} variant="secondary" className="w-full sm:w-auto whitespace-normal h-auto min-h-10 bg-sky-700 text-white hover:bg-sky-800">
        Redefinir senha do administrador principal
      </Button>
      {!target && !loadError && <p role="status" className="text-sm text-slate-400">Verificando o administrador vinculado...</p>}
      {target?.eligible === false && <p role="status" data-testid="initial-admin-reset-blocked" className="text-sm text-amber-200">{target.reason}</p>}
      {target?.can_link && <><AccessAdminBootstrap tenantId={tenantId} onBusyChange={onBusyChange} onCreated={() => setRevision((r) => r + 1)} /><PrincipalAdminLink tenantId={tenantId} onBusyChange={onBusyChange} onLinked={() => setRevision((r) => r + 1)} /></>}
      {loadError && <div role="alert" className="text-sm text-amber-200">{loadError} <Button type="button" variant="ghost" onClick={() => setRevision((r) => r + 1)}>Tentar novamente</Button></div>}
      <Dialog open={open} onOpenChange={(value) => { if (!value) close(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle>Redefinir senha do administrador principal</DialogTitle>
            <DialogDescription className="text-slate-300">
              Somente a conta identificada abaixo será afetada. Suas sessões atuais serão encerradas.
            </DialogDescription>
          </DialogHeader>
          {target?.eligible && <div data-testid="initial-admin-reset-target" className="text-sm space-y-1">
            <p>Empresa: <strong>{target.tenant.name}</strong></p>
            <p>Administrador: <strong>{target.user.name}</strong> — {target.user.email}</p>
            <p className="text-xs text-slate-400">ID da conta: {target.user.id}</p>
          </div>}
          <form onSubmit={submit} className="space-y-4" data-testid="initial-admin-reset-form">
            <div><Label htmlFor="initial-admin-new-password">Nova senha</Label>
              <Input id="initial-admin-new-password" data-testid="initial-admin-new-password"
                type="password" autoComplete="new-password" value={password} disabled={busy || uncertain}
                onChange={(e) => setPassword(e.target.value)} /></div>
            <div><Label htmlFor="initial-admin-confirm-password">Confirmar nova senha</Label>
              <Input id="initial-admin-confirm-password" data-testid="initial-admin-confirm-password"
                type="password" autoComplete="new-password" value={confirm} disabled={busy || uncertain}
                onChange={(e) => setConfirm(e.target.value)} /></div>
            <p className="text-xs text-slate-400">Mínimo de 6 caracteres e máximo de 72 bytes em UTF-8. A senha atual nunca é exibida.</p>
            {error && <p role="alert" data-testid="initial-admin-reset-error" className="text-sm text-amber-200">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="ghost" disabled={busy} onClick={close}>Cancelar</Button>
              <Button type="submit" data-testid="initial-admin-reset-submit" disabled={busy || !target?.eligible}>
                {busy ? "Redefinindo..." : uncertain ? "Repetir confirmação" : "Confirmar redefinição"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
